import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, CloudRain, Loader2, ShieldAlert, ShieldCheck, ShieldOff, Umbrella } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import pestModelService from '../../services/pestModels';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz } from '../../utils/timezone';
import type { FarmProtection, ProtectionUnit, ProtectionInfection, InfectionStatus } from '../../types/pest';

const at = (ms: number, tz: string) => formatDateTimeInTz(new Date(ms).toISOString(), tz);

const REASON: Record<string, string> = {
  no_series: 'No risk series has been worked out for this farm yet.',
  no_rain_recorded: 'No rain has been recorded for this season yet, so wash-off cannot be judged.',
  farm_not_found: 'Farm not found.',
  not_infection_model: 'Protection is only tracked for infection-model diseases.',
};

const INFECTION_LABEL: Record<InfectionStatus, { text: string; cls: string }> = {
  protected: { text: 'covered by protectant', cls: 'text-emerald-700' },
  cured: { text: 'cured', cls: 'text-emerald-700' },
  curative_window: { text: 'curative window open', cls: 'text-red-700 font-medium' },
  missed: { text: 'not covered — window closed, scout for lesions', cls: 'text-red-700' },
  forecast_protected: { text: 'forecast, protected', cls: 'text-neutral-600' },
  forecast_unprotected: { text: 'forecast, not protected', cls: 'text-amber-700 font-medium' },
};

/**
 * Is each planting protected against this disease, and if an infection got
 * through, how long is left to cure it. Plantings in the same position — same
 * spray, same infections — are shown together.
 */
export default function PestProtectionPanel({ farmId, pestId, reloadKey }: { farmId: number; pestId: number; reloadKey?: unknown }) {
  const [data, setData] = useState<FarmProtection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await pestModelService.getProtection(farmId, pestId)); setError(null); }
    catch (e) { setError(errorMessage(e)); setData(null); }
    finally { setLoading(false); }
  }, [farmId, pestId]);
  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Umbrella className="h-5 w-5 text-teal-600" /> Protection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && !data && <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking sprays against the rain…</div>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {data && !data.available && <p className="text-sm text-neutral-500">{REASON[data.reason] ?? 'Not available.'}</p>}
        {data && data.available && <ProtectionBody data={data} />}
      </CardContent>
    </Card>
  );
}

function ProtectionBody({ data }: { data: Extract<FarmProtection, { available: true }> }) {
  const tz = data.timezone;
  const groups = groupUnits(data.units);
  return (
    <>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-800">
          <CloudRain className="h-3.5 w-3.5" /> {data.rain_last_72h_mm} mm last 3 days
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-800">
          <CloudRain className="h-3.5 w-3.5" /> {data.rain_next_72h_mm} mm forecast next 3 days
        </span>
      </div>

      {data.match_basis === 'any_fungicide' && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          No registration target pests are linked to {data.pest_name}, so every fungicide sprayed counts as cover.
          Link them on the pest&apos;s{' '}
          <Link to={`/spray/pests/${data.pest_model_id}?tab=products`} className="underline">Products tab</Link>{' '}
          so only products registered against it count.
        </p>
      )}

      {!data.units.length && <p className="text-sm text-neutral-500">No plantings of a host crop on this farm.</p>}

      {groups.map((g) => <UnitGroup key={g.key} units={g.units} tz={tz} />)}

      <p className="text-xs text-neutral-500 leading-relaxed">
        A protectant is counted as washed off once the rain since it went on reaches its figure
        ({data.default_washoff_mm} mm unless the chemical says otherwise). Curative windows count from the start
        of wetting and use the short end of the published range; warm weather shortens them further. A product&apos;s
        own figures can be set on its chemical page.
      </p>
    </>
  );
}

function UnitGroup({ units, tz }: { units: ProtectionUnit[]; tz: string }) {
  const u = units[0];
  const lp = u.last_protectant;
  const recent = u.infections.filter((i) => i.status !== 'forecast_protected');
  const { icon, tone, headline } = headlineFor(u, tz);

  return (
    <div className={`rounded-xl border px-3 py-3 space-y-2 ${tone}`}>
      <div className="flex items-start gap-2">
        {icon}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-neutral-900 break-words">{headline}</div>
          <div className="text-xs text-neutral-600 break-words">
            {units.map((x) => (x.block_name ? `${x.block_name} — ${x.name}` : x.name)).join(', ')}
          </div>
        </div>
      </div>

      {u.action.kind === 'curative_now' && <CurativeWindows inf={u.infections.find((i) => i.status === 'curative_window' && i.start === (u.action as { infection_start: number }).infection_start)} tz={tz} />}

      {lp ? (
        <div className="text-xs text-neutral-700 space-y-1">
          <div className="break-words">
            Last protectant: <span className="font-medium">{lp.products.join(' + ')}</span> · {lp.spray_name}, {at(lp.applied_at, tz)}
          </div>
          <RainBar lp={lp} />
        </div>
      ) : (
        <div className="text-xs text-neutral-600">No protectant registered against it has been sprayed here this season.</div>
      )}

      {recent.length > 0 && (
        <ul className="text-xs space-y-0.5">
          {recent.map((i) => (
            <li key={i.start} className="break-words">
              <span className="text-neutral-600">{capitalise(i.severity)} infection {i.is_forecast ? 'forecast from' : 'from'} {at(i.start, tz)} — </span>
              <span className={INFECTION_LABEL[i.status].cls}>{INFECTION_LABEL[i.status].text}</span>
              {i.cured_by && <span className="text-neutral-500"> by {i.cured_by.products.join(' + ')}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CurativeWindows({ inf, tz }: { inf: ProtectionInfection | undefined; tz: string }) {
  if (!inf) return null;
  return (
    <ul className="text-xs space-y-1 bg-white/70 rounded-lg px-2.5 py-2 border border-red-200">
      {inf.windows.map((w) => (
        <li key={w.key} className={`break-words ${w.open ? 'text-red-800' : 'text-neutral-400 line-through'}`}>
          <span className="font-medium">{w.label}</span> ({w.kickback_hours} h) — {w.open ? 'until' : 'closed'} {at(w.closes_at, tz)}
          {w.products.length > 0 && <span className="text-neutral-600"> · {w.products.join(', ')}</span>}
        </li>
      ))}
    </ul>
  );
}

function RainBar({ lp }: { lp: NonNullable<ProtectionUnit['last_protectant']> }) {
  const pct = Math.min(100, (lp.rain_since_mm / lp.washoff_mm) * 100);
  const forecastPct = Math.min(100, (lp.rain_with_forecast_mm / lp.washoff_mm) * 100);
  return (
    <div>
      <div className="relative h-2 rounded-full bg-neutral-200 overflow-hidden" aria-hidden>
        <div className="absolute inset-y-0 left-0 bg-sky-200" style={{ width: `${forecastPct}%` }} />
        <div className={`absolute inset-y-0 left-0 ${pct >= 100 ? 'bg-red-500' : 'bg-sky-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-0.5 text-neutral-500">
        {lp.rain_since_mm} of {lp.washoff_mm} mm since spraying
        {lp.washoff_source === 'default' ? ' (default wash-off)' : ''}
        {lp.rain_with_forecast_mm > lp.rain_since_mm && ` · ${lp.rain_with_forecast_mm} mm with the forecast`}
      </div>
    </div>
  );
}

function headlineFor(u: ProtectionUnit, tz: string) {
  const a = u.action;
  switch (a.kind) {
    case 'curative_now':
      return { icon: <ShieldAlert className="h-5 w-5 text-red-600 shrink-0" />, tone: 'border-red-200 bg-red-50', headline: `Curative window open — closes ${at(a.closes_at, tz)}` };
    case 'protect_before':
      return { icon: <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />, tone: 'border-amber-200 bg-amber-50', headline: `Infection forecast ${at(a.infection_start, tz)} — protect before it` };
    case 'reprotect':
      return {
        icon: <ShieldOff className="h-5 w-5 text-amber-600 shrink-0" />,
        tone: 'border-amber-200 bg-amber-50/60',
        headline: u.state === 'washed_off' && u.unprotected_since ? `Washed off ${at(u.unprotected_since, tz)}` : 'Not protected this season',
      };
    case 'washoff_forecast':
      return { icon: <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0" />, tone: 'border-amber-200 bg-white', headline: `Protected — forecast to wash off ${at(a.washoff_at, tz)}` };
    default:
      return { icon: <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />, tone: 'border-emerald-200 bg-emerald-50/50', headline: 'Protected' };
  }
}

/** Plantings in the same position share one box. */
function groupUnits(units: ProtectionUnit[]) {
  const order = ['curative_now', 'protect_before', 'reprotect', 'washoff_forecast', 'none'];
  const map = new Map<string, ProtectionUnit[]>();
  for (const u of units) {
    const key = JSON.stringify([
      u.state, u.action, u.last_protectant?.spray_id ?? null, u.unprotected_since,
      u.infections.map((i) => [i.start, i.status]),
    ]);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(u);
  }
  return [...map.entries()]
    .map(([key, us]) => ({ key, units: us }))
    .sort((a, b) => order.indexOf(a.units[0].action.kind) - order.indexOf(b.units[0].action.kind));
}

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
